import React, { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import BottomNav from "../components/BottomNav"
import API from "../services/api"
import { getUser, secureStorage } from '../utils/storage'
import { useTheme } from "../context/ThemeContext"
import BannerSlider from "../components/BannerSlider"


const Profile = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // ✅ Real user data from secure storage
  const storedUser = getUser()
  const { isDarkMode, toggleTheme } = useTheme()
  const [user, setUser] = useState({
    id: storedUser.id,
    firstName: storedUser.firstName || storedUser.first_name || "User",
    lastName: storedUser.lastName || storedUser.last_name || "",
    mobile: storedUser.mobile || "9999999999",
    balance: storedUser.wallet || storedUser.balance || "0",
    picture: storedUser.picture || null,
    matchesPlayed: 0,
    contestsWon: 0
  })

  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    mobile: user.mobile,
    picture: user.picture || ""
  })

  // ✅ MATCH HISTORY MODAL STATE
  const [showMatchesModal, setShowMatchesModal] = useState(false)
  const [recentMatches, setRecentMatches] = useState([])
  const [loadingMatches, setLoadingMatches] = useState(false)

  const handleFetchMatches = async () => {
    setShowMatchesModal(true)
    setLoadingMatches(true)
    try {
      const res = await API.get(`/matches/user-completed/${user.id}`)
      setRecentMatches(res.data)
    } catch (err) {
      console.error("Match fetch failed:", err)
    } finally {
      setLoadingMatches(false)
    }
  }

  // ✅ 1. Sync data with backend on mount
  React.useEffect(() => {
    const syncProfile = async () => {
      try {
        const res = await API.get(`/auth/me?userId=${user.id}`);
        if (res.data.user) {
          const freshUser = res.data.user;
          const updated = {
            ...user,
            firstName: freshUser.firstName,
            lastName: freshUser.lastName,
            mobile: freshUser.mobile,
            balance: freshUser.wallet,
            picture: freshUser.picture
          };
          setUser(updated);
          secureStorage.setItem("winrox11_user", freshUser);
        }
      } catch (err) {
        console.error("Profile sync failed:", err);
      }
    };
    syncProfile();
  }, []); // eslint-disable-line

  // ✅ 2. React to user state changes (e.g. after update)
  React.useEffect(() => {
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      mobile: user.mobile,
      picture: user.picture || ""
    })
  }, [user])

  const handleLogout = () => {
    localStorage.removeItem("winrox11_token")
    secureStorage.removeItem("winrox11_user")
    alert("Logged out successfully!")
    navigate("/login", { replace: true })
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await API.put("/auth/update-profile", {
        userId: user.id,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        mobile: editForm.mobile,
        picture: editForm.picture
      });
      if (res.data.success) {
        const updatedUser = res.data.user;
        setUser({
          ...updatedUser,
          balance: updatedUser.wallet, // Map wallet to balance for local state if needed
          matchesPlayed: user.matchesPlayed,
          contestsWon: user.contestsWon
        });
        secureStorage.setItem("winrox11_user", updatedUser);
        setIsEditing(false);
        alert("Profile updated!");
      }
    } catch (err) {
      console.error(err);
      alert("Update failed");
    }
  }

  // ✅ Auto-Open Share on return from deposit
  useEffect(() => {
    if (location.state?.openShare) {
      setTimeout(() => {
        handleShare();
        // Clear state to prevent loop
        navigate(location.pathname, { replace: true, state: {} });
      }, 500);
    }
  }, [location.state]);

  const handleShare = () => {
    // Check if user has added payment (balance > 0)
    if (Number(user.balance) <= 0) {
      alert("Please add money to your wallet first to unlock Sharing & Rewards! 💰");
      navigate("/wallet", { state: { fromShare: true } });
      return;
    }

    if (navigator.share) {
      navigator.share({
        title: 'WinroX11 Fantasy Cricket',
        text: 'Join WinroX11 Fantasy Cricket and win a 3D/2N Goa Trip! Download now and start winning.',
        url: window.location.origin + '?ref=' + user.id
      }).catch(console.error);
    } else {
      alert("Referral Link: " + window.location.origin + '?ref=' + user.id + "\n\nCopy and share with your friends!");
    }
  }

  return (
    <div className="bg-theme-bg text-theme-text min-h-screen pb-20 font-sans">

      {/* 🔝 TOP HEADER */}
      <div className="p-4 bg-theme-card border-b border-theme-border flex justify-between items-center sticky top-0 z-50">
        <button
          onClick={() => navigate("/")}
          className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2 className="font-bold text-lg text-theme-text tracking-tight uppercase">My Profile</h2>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="text-accent text-[12px] font-bold bg-accent/10 px-4 py-1.5 rounded-full border border-accent/20 transition-all hover:bg-accent/20"
        >
          {isEditing ? "Cancel" : "Edit"}
        </button>
      </div>

      <div className="p-4 space-y-6">

        {/* 👤 ID CARD SECTION (Minimal Redesign) */}
        <div className="bg-theme-card rounded-theme shadow-theme-sm p-6 flex items-center space-x-5 border border-theme-border relative overflow-hidden">
          <div className="w-20 h-20 bg-theme-bg border border-theme-border rounded-full flex items-center justify-center shadow-inner shrink-0 overflow-hidden relative z-10 font-bold uppercase text-2xl">
            {user.picture ? (
              <img src={user.picture} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <span className="text-theme-text">
                {(user.firstName?.[0] || "") + (user.lastName?.[0] || "")}
              </span>
            )}
          </div>

          <div className="flex-1 relative z-10">
            {isEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-2">
                <input
                  className="bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm w-full outline-none focus:border-accent"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  placeholder="First Name"
                />
                <input
                  className="bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm w-full outline-none focus:border-accent"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  placeholder="Last Name"
                />
                <input
                  className="bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm w-full outline-none focus:border-accent"
                  value={editForm.mobile}
                  onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                  placeholder="Mobile Number"
                />
                <button type="submit" className="bg-accent text-white text-[11px] font-bold px-4 py-2 rounded-lg uppercase tracking-wider w-full shadow-sm transition-all hover:brightness-105 active:scale-95">Save Changes</button>
              </form>
            ) : (
              <>
                <h3 className="text-xl font-bold tracking-tight text-theme-text break-words leading-none">{user.firstName} {user.lastName}</h3>
                <p className="text-sm text-theme-text-sec mt-1.5 font-medium">+91 {user.mobile}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 bg-accent/10 px-3 py-1 rounded-full border border-accent/20">
                  <span className="text-[10px] font-bold text-accent uppercase tracking-wide">🏆 Pro League Player</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 💰 WALLET & STATS STRIP */}
        <div className="grid grid-cols-2 gap-4">
          <div
            onClick={() => navigate("/wallet")}
            className="bg-theme-card/50 rounded-2xl p-4 border border-theme-border shadow-lg flex flex-col justify-center items-center cursor-pointer active:scale-95 transition-all"
          >
            <p className="text-[9px] text-theme-text-sec font-bold mb-1 uppercase tracking-widest text-center">Wallet Balance</p>
            <p className="text-xl font-black text-green-400">₹{user.balance}</p>
          </div>
          <div
            onClick={handleFetchMatches}
            className="bg-theme-card/50 rounded-2xl p-4 border border-theme-border shadow-lg flex flex-col justify-center items-center cursor-pointer active:scale-95 transition-all group/matches"
          >
            <div className="flex justify-between w-full text-center text-sm">
              <div className="flex-1">
                <p className="font-black text-theme-text text-lg">{user.matchesPlayed}</p>
                <p className="text-[9px] text-theme-text-sec font-bold uppercase tracking-tighter">Matches</p>
              </div>
              <div className="w-px h-8 bg-zinc-800 mx-1 opacity-50"></div>
              <div className="flex-1">
                <p className="font-black text-yellow-500 text-lg">{user.contestsWon}</p>
                <p className="text-[9px] text-theme-text-sec font-bold uppercase tracking-tighter">Won</p>
              </div>
            </div>
          </div>
        </div>

        {/* ⚙️ OPTIONS MENU */}
        <div className="bg-theme-card rounded-theme shadow-theme-sm border border-theme-border overflow-hidden">
          <ul className="divide-y divide-theme-border">
            {/* Theme Toggle Option */}
            <li className="p-4 active:bg-theme-bg flex justify-between items-center transition-all cursor-pointer" onClick={toggleTheme}>
              <div className="flex items-center space-x-3">
                <span className="text-xl">{isDarkMode ? "☀️" : "🌙"}</span>
                <span className="font-bold text-theme-text text-sm">{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
              </div>
              <div className={`w-10 h-5 rounded-full relative transition-colors duration-300 ${isDarkMode ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-700'}`}>
                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all duration-300 ${isDarkMode ? 'left-6' : 'left-1'}`} />
              </div>
            </li>

            <li className="p-4 active:bg-theme-bg transition cursor-pointer flex justify-between items-center" onClick={() => navigate("/my-teams")}>
              <div className="flex items-center space-x-3">
                <span className="text-xl">🏏</span>
                <span className="font-bold text-theme-text text-sm">My Saved Teams</span>
              </div>
              <span className="text-theme-text-sec text-xs">→</span>
            </li>

            <li className="p-4 active:bg-theme-bg transition cursor-pointer flex justify-between items-center" onClick={() => navigate("/wallet")}>
              <div className="flex items-center space-x-3">
                <span className="text-xl">💳</span>
                <span className="font-bold text-theme-text text-sm">Account & Transactions</span>
              </div>
              <span className="text-theme-text-sec text-xs">→</span>
            </li>

            <li className="p-4 active:bg-theme-bg transition cursor-pointer flex justify-between items-center" onClick={() => navigate("/help")}>
              <div className="flex items-center space-x-3">
                <span className="text-xl">🎧</span>
                <span className="font-bold text-theme-text text-sm">Help & Support</span>
              </div>
              <span className="text-theme-text-sec text-xs">→</span>
            </li>
          </ul>
        </div>

        {/* 🏖️ GOA TRIP SHARE PROMOTION (Minimal Redesign) */}
        <div
          onClick={() => navigate("/goa-leaderboard")}
          className="relative overflow-hidden rounded-theme bg-theme-card p-6 shadow-theme-sm border border-theme-border active:scale-[0.98] transition-all cursor-pointer text-center"
        >
          <div className="relative z-10 flex flex-col items-center">
            <div className="mb-4 text-5xl">🏖️</div>
            <h3 className="text-2xl font-bold uppercase tracking-tight text-theme-text mb-1">
              Win a <span className="text-accent">Free Goa Trip</span>
            </h3>
            <p className="text-[10px] font-medium text-theme-text-sec uppercase tracking-widest mb-6">
              Maximum Share = Higher Win Chance
            </p>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShare();
              }}
              className="w-full bg-accent text-white py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider shadow-sm transition-all hover:brightness-105 active:scale-95"
            >
              Share & Win Now ✈️
            </button>
          </div>
        </div>

        {/* 📢 AD BANNERS */}
        <div className="mt-4">
          <BannerSlider />
        </div>

        {/* 🚪 LOGOUT BUTTON */}
        <button
          onClick={handleLogout}
          className="w-full mt-2 bg-red-500/10 text-red-500 py-3.5 rounded-xl font-bold text-xs tracking-wider uppercase border border-red-500/20 shadow-theme-sm transition-all active:scale-95"
        >
          Logout Session
        </button>
      </div>

      {/* 📅 RECENT MATCHES MODAL */}
      {showMatchesModal && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-theme-bg/80 backdrop-blur-sm p-0 animate-[fadeIn_0.3s_ease-out]" onClick={() => setShowMatchesModal(false)}>
          <div className="bg-theme-card w-full max-w-md rounded-t-[40px] shadow-theme-lg overflow-hidden animate-[slideUp_0.3s_ease-out] h-[75vh] flex flex-col border-t-4 border-accent" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-theme-border flex justify-between items-center bg-theme-card shrink-0">
              <h3 className="font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-3 text-theme-text-sec">
                <span className="w-2 h-2 bg-accent rounded-full animate-pulse"></span>
                Completed Matches
              </h3>
              <button 
                onClick={() => setShowMatchesModal(false)} 
                className="w-8 h-8 flex items-center justify-center bg-theme-bg rounded-full border border-theme-border text-theme-text-sec font-bold"
              >✕</button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {loadingMatches ? (
                <div className="h-full flex flex-col items-center justify-center gap-4 opacity-50">
                  <div className="w-9 h-9 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest">Fetching History...</p>
                </div>
              ) : recentMatches.length > 0 ? (
                recentMatches.map((m, idx) => (
                  <div key={m.match_id} className="bg-theme-bg border border-theme-border rounded-theme p-4 shadow-theme-sm relative">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-[9px] font-bold text-theme-text-sec uppercase tracking-widest opacity-40">{m.date}</span>
                      <span className="text-[8px] font-bold bg-theme-card text-accent px-2 py-0.5 rounded-sm uppercase tracking-widest border border-accent/10">Completed</span>
                    </div>
                    <div className="flex font-bold text-theme-text text-xs items-center justify-between">
                      <div className="flex items-center gap-3 w-[40%]">
                        <div className="w-10 h-10 rounded-xl bg-theme-bg border border-theme-border flex items-center justify-center p-1.5 shadow-theme-sm"><img src={m.team1_logo} className="w-full h-full object-contain" alt="" /></div>
                        <span className="truncate uppercase">{m.team1_short || m.team1?.substring(0, 3)}</span>
                      </div>
                      <span className="text-[10px] font-bold text-theme-text-sec opacity-20 italic">VS</span>
                      <div className="flex items-center flex-row-reverse gap-3 w-[40%]">
                        <div className="w-10 h-10 rounded-xl bg-theme-bg border border-theme-border flex items-center justify-center p-1.5 shadow-theme-sm"><img src={m.team2_logo} className="w-full h-full object-contain" alt="" /></div>
                        <span className="truncate uppercase text-right">{m.team2_short || m.team2?.substring(0, 3)}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-full flex flex-col items-center justify-center opacity-30">
                  <span className="text-6xl mb-4">🏏</span>
                  <p className="text-[10px] font-black uppercase tracking-widest">No Completed Matches Yet</p>
                </div>
              )}
            </div>

            <div className="p-6 bg-theme-card border-t border-theme-border shrink-0">
               <button onClick={() => setShowMatchesModal(false)} className="w-full py-4 bg-accent text-white rounded-2xl text-[10px] font-bold uppercase tracking-widest shadow-theme-lg active:scale-[0.98] transition-all">Close History</button>
            </div>
          </div>
        </div>
      )}

      <BottomNav activeTab="profile" />
      <style dangerouslySetInnerHTML={{ __html: `@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } } @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }` }} />
    </div>
  )
}

export default Profile
